import React, { useState, useEffect } from 'react';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ConfirmationDialog } from '@travelity/ui';
import { copyToClipboard } from '@travelity/utils';
import { useCopyOrderPublic } from '@travelity/api';
import { useToast } from '../../hooks';

export interface CopyUrlModalProps {
    open: boolean;
    onClose: () => void;
    orderId?: string;
}

export const CopyUrlModal: React.FC<CopyUrlModalProps> = ({
    open,
    onClose,
    orderId,
}) => {
    const { showSuccess } = useToast();
    const [isCopying, setIsCopying] = useState(false);
    const [url, setUrl] = useState('');

    const {
        mutate: copyUrl,
        isLoading,
        isError,
        reset,
    } = useCopyOrderPublic({
        onSuccess: fetchedUrl => {
            setUrl(fetchedUrl);
        },
    });

    useEffect(() => {
        if (open && orderId) {
            copyUrl({ orderId });
        }

        return () => {
            if (!open) {
                setUrl('');
                reset();
            }
        };
    }, [open, orderId, copyUrl, reset]);

    const handleCopy = async () => {
        setIsCopying(true);
        try {
            await copyToClipboard(url);
            showSuccess('ORDER_COPY_SUCCESS');
        } catch (error) {
            console.error('Failed to copy URL', error);
        } finally {
            setIsCopying(false);
        }
    };

    return (
        <ConfirmationDialog
            open={open}
            handleConfirm={onClose}
            title="Order URL"
            content={
                isError
                    ? 'Failed to get order URL. Please try again.'
                    : 'You can copy this URL to share with others'
            }
            confirmText="Close"
            isLoading={isLoading}
        >
            <Box sx={{ mt: 2, mb: 2, width: { xs: '100%', sm: '380px' } }}>
                {isError ? (
                    <Box sx={{ color: 'error.main', mb: 2 }}>
                        There was an error retrieving the URL. Please close and
                        try again.
                    </Box>
                ) : (
                    <TextField
                        fullWidth
                        value={url}
                        variant="outlined"
                        placeholder={isLoading ? 'Loading URL...' : ''}
                        sx={{
                            '& input': {
                                pr: 0,
                            },
                        }}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={handleCopy}
                                        disabled={
                                            isCopying || !url || isLoading
                                        }
                                        title="Copy to clipboard"
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </Box>
        </ConfirmationDialog>
    );
};

export default CopyUrlModal;
